import { API_URL, isBrowser, isDevelopment } from "appConfiguration";
import { copyTextToClipboard } from "core/utils/copyTextToClipboard";
import i18n, { type InitOptions } from "i18next";
import FetchBackend from "i18next-fetch-backend";
import { initReactI18next } from "react-i18next";

const shouldHandleMissingKeys = isBrowser && isDevelopment;

const namespaceMap = new Map<string, Map<string, string>>();

export function copyTranslationsToClipboard() {
  if (namespaceMap.size === 0) {
    return;
  }
  const output = [];

  for (const [namespace, keyMap] of namespaceMap) {
    const comment = `// ${namespace}`;
    const keysString = [...keyMap]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, fallbackValue]) => `"${key}":"${fallbackValue}"`)
      .join(",\n");
    const formattedString = `${comment}\n${keysString},`;
    output.push(formattedString);
  }
  copyTextToClipboard(output.join("\n\n"));
}

export function getSerializedMissingTranslations(): Record<
  string,
  Record<string, string>
> | null {
  if (namespaceMap.size === 0) {
    return null;
  }
  return Object.fromEntries(
    [...namespaceMap].map(([ns, keyMap]) => [ns, Object.fromEntries(keyMap)])
  );
}

export const namespaces = [
  "Fixhub",
  "PaymentRequest",
  "NewsEvents",
  "DocumentManagementSystem",
  "AuthorizeNet",
] as const;

const config: InitOptions = {
  backend: {
    loadPath: (lng: string, ns: string) => {
      const url = new URL("/api/app/localization", API_URL);
      url.searchParams.set("locale", lng);
      url.searchParams.set("resource", ns);
      return url.href;
    },
  },
  fallbackLng: isDevelopment ? false : "en",
  fallbackNS: ["Fixhub"],
  debug: false,
  ns: ["Fixhub", "DocumentManagementSystem", "NewsEvents"],
  defaultNS: "Fixhub",
  lng: "en",
  nsSeparator: ";",
  saveMissing: shouldHandleMissingKeys,
  missingKeyHandler: (lng, ns, key, fallbackValue) => {
    if (!lng.includes("en")) {
      return;
    }
    const missingKeyMap = namespaceMap.get(ns) || new Map<string, string>();
    const value =
      key !== fallbackValue
        ? fallbackValue
        : key.slice(key.lastIndexOf(".") + 1);
    missingKeyMap.set(key, value);
    namespaceMap.set(ns, missingKeyMap);
    // eslint-disable-next-line no-console
    console.debug(
      `%cMissing Key "${key}"`,
      "color:red;font-weight:bold;",
      namespaceMap
    );
  },
  interpolation: {
    escapeValue: false,
    format(value, format, lng, edit) {
      const formatParams = edit?.formatParams;
      if (format === "number") {
        return new Intl.NumberFormat(lng, formatParams).format(value);
      }
      if (format === "datetime") {
        return new Intl.DateTimeFormat(lng, formatParams).format(value);
      }
      if (format === "currency") {
        return new Intl.NumberFormat(lng, {
          style: "currency",
          currency: "CAD",
          currencyDisplay: "narrowSymbol",
        }).format(value);
      }
      return value;
    },
  },
};

i18n.use(FetchBackend).use(initReactI18next).init(config);

export default i18n;
